import { useState, Component, ReactNode, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent, TabsBadge } from '@components/ui';
import {
  CARD,
  CTA,
  METRIC_LABEL,
  SECTION_NUM,
  SECTION_TITLE,
  SECTION_DESC,
  FORM_LABEL,
  FIELD_HINT,
  INSET_PANEL,
  SETTING_ROW,
  SETTING_ICON,
  TONE_SURFACE,
  TONE_BAR,
} from '@components/ui/designTokens';
// SUBSCRIPTIONS_DISABLED: import { CurrentSubscriptionCard } from '@features/subscriptions/components/CurrentSubscriptionCard';
import { FiscalInfoTab } from '@features/certificates/components/FiscalInfoTab';
import { TemplatesTab } from '@features/templates/components/TemplatesTab';
import { useCertificates } from '@features/certificates/hooks/useCertificates';
import {
  Cog6ToothIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';

/**
 * Configuración Administrativa — Claude Design · Configuracion.dc.html
 *
 * El diseño reordena la pantalla (pestañas en capsula, franja de estado,
 * secciones numeradas con la etiqueta a la izquierda y los campos a la
 * derecha, barra de guardado flotante) pero no cambia que se muestra: los tres
 * ajustes de la pestaña General siguen siendo los mismos tres controles, con
 * el mismo texto y el mismo `disabled` que ya tenian, y las pestañas Fiscal y
 * Templates siguen montando FiscalInfoTab y TemplatesTab tal cual.
 *
 * La franja de estado solo muestra datos reales:
 *   - Certificados sale de useCertificates() (los mismos certificateAuth /
 *     certificateSig que lee la pestaña Fiscal; React Query comparte la cache,
 *     asi que no hay una segunda llamada);
 *   - Notificaciones y Seguridad salen del estado de los dos toggles.
 * La tarjeta "Estado del entorno" del mock (OPERATIVO · ultima sincronizacion
 * hace 3 min · Ambiente Producción) quedo fuera: no hay endpoint de salud ni de
 * ambiente detras de esos valores, e inventarlos seria peor que no mostrarlos.
 */

// Error Boundary for debugging
class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-xl border border-[#b8503a]/25 bg-[#b8503a]/[.06] p-4">
          <h3 className="font-bold text-[#a24030]">Error en Templates:</h3>
          <p className="mt-1 text-sm text-[#a24030]">{this.state.error?.message}</p>
          <pre className="mt-2 overflow-auto font-mono text-xs text-secondary-500">
            {this.state.error?.stack}
          </pre>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Checkbox del diseño (.cbx): cuadrado navy con tilde blanca. El `<input>`
 * sigue siendo un checkbox de verdad — solo se oculta visualmente y se dibuja
 * con el hermano, asi que el `disabled` y el `id`/`htmlFor` se conservan.
 */
function DesignCheckbox({
  id,
  checked,
  onChange,
  disabled,
  'data-testid': testId,
}: {
  id: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  'data-testid'?: string;
}) {
  return (
    <span className="relative inline-flex h-5 w-5 shrink-0">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        data-testid={testId}
        className="peer sr-only"
      />
      <label
        htmlFor={id}
        aria-hidden="true"
        className="h-5 w-5 cursor-pointer rounded-md border-[1.5px] border-secondary-300 bg-white transition-colors hover:border-secondary-400 peer-checked:border-ink-850 peer-checked:bg-ink-850 peer-disabled:cursor-not-allowed peer-disabled:opacity-60 dark:border-secondary-600 dark:bg-secondary-800"
      />
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="pointer-events-none absolute left-[3px] top-[3px] h-3.5 w-3.5 text-white opacity-0 transition-opacity peer-checked:opacity-100"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20 6L9 17l-5-5" />
      </svg>
    </span>
  );
}

/**
 * Toggle del diseño (.toggle) sobre un checkbox real. Se usa donde la pantalla
 * ya tenia un checkbox: el control no cambia de tipo ni deja de estar
 * deshabilitado, solo se dibuja como interruptor.
 */
function DesignToggle({
  id,
  checked,
  onChange,
  disabled,
}: {
  id: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}) {
  return (
    <span className="relative inline-flex h-[22px] w-10 shrink-0">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="peer sr-only"
      />
      <label
        htmlFor={id}
        aria-hidden="true"
        className="h-[22px] w-10 cursor-pointer rounded-full bg-[#e2e2e4] transition-colors peer-checked:bg-gradient-to-br peer-checked:from-teal-500 peer-checked:to-[#6b239e] peer-disabled:cursor-not-allowed peer-disabled:opacity-60 dark:bg-secondary-600"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-0.5 top-0.5 h-[18px] w-[18px] rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,.2)] transition-transform peer-checked:translate-x-[18px]"
      />
    </span>
  );
}

/** Chip de estado de una seccion (el pill de la esquina de cada tarjeta). */
function StatusPill({
  tone,
  children,
}: {
  tone: 'ok' | 'warn' | 'muted';
  children: ReactNode;
}) {
  const tones = {
    ok: 'bg-[#0d9488]/10 text-[#0d9488]',
    warn: 'bg-accent-500/[.12] text-[#a15f0a]',
    muted: 'bg-secondary-100 text-secondary-500 dark:bg-secondary-700 dark:text-secondary-300',
  };
  const dots = { ok: 'bg-[#0d9488]', warn: 'bg-accent-500', muted: 'bg-secondary-400' };

  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-bold tracking-[0.12em] ${tones[tone]}`}
    >
      <span className={`h-[5px] w-[5px] rounded-full ${dots[tone]}`} />
      {children}
    </span>
  );
}

/** Cabecera de seccion: numero, titulo y descripcion. */
/**
 * Rotulo de seccion. El icono con tono propio es lo que las diferencia: con
 * solo "01 · GENERAL / 02 · CANALES" las tarjetas se veian iguales y habia
 * que leer el titulo para saber en cual estabas.
 */
function SectionHead({
  num,
  title,
  desc,
  icon,
  tone = 'ink',
}: {
  num: string;
  title: string;
  desc: string;
  icon?: React.ReactNode;
  tone?: 'ink' | 'teal' | 'violet' | 'amber';
}) {
  const tones: Record<string, string> = {
    ink: 'bg-ink-850/[.07] text-ink-850 dark:bg-white/10 dark:text-white',
    teal: 'bg-teal-500/[.12] text-[#0d9488] dark:bg-teal-500/25 dark:text-teal-200',
    violet: 'bg-[#6b239e]/[.12] text-[#6b239e] dark:bg-[#6b239e]/30 dark:text-[#cfa8ec]',
    amber: 'bg-accent-500/[.14] text-[#a15f0a] dark:bg-accent-500/25 dark:text-accent-200',
  };
  return (
    <div>
      <div className="flex items-center gap-2.5">
        {icon && (
          <span
            aria-hidden="true"
            className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] ${tones[tone]}`}
          >
            {icon}
          </span>
        )}
        <div className={SECTION_NUM}>{num}</div>
      </div>
      <h2 className={`mt-2.5 ${SECTION_TITLE}`}>{title}</h2>
      <p className={`mt-2 ${SECTION_DESC}`}>{desc}</p>
    </div>
  );
}

/** Layout del diseño (.split): rotulo de seccion a la izquierda, campos a la derecha. */
const SPLIT = 'grid items-start gap-5 xl:grid-cols-[260px_minmax(0,1fr)] xl:gap-10';

export function AdminSettingsPage() {
  const [searchParams] = useSearchParams();
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [autoApprove, setAutoApprove] = useState(false);
  const [invoicePrefix, setInvoicePrefix] = useState('FE');
  const [defaultTaxRate, setDefaultTaxRate] = useState('7');

  /*
   * Estado de certificados para la franja superior y para el badge de la
   * pestaña Fiscal. Es la misma query que ya usa FiscalInfoTab (queryKey
   * ['certificates']), asi que React Query reutiliza la cache.
   */
  const { data: certificatesData, isLoading: isLoadingCerts } = useCertificates();
  const apiCertificate = certificatesData?.certificate;
  const certsConfigured = [apiCertificate?.certificateAuth, apiCertificate?.certificateSig].filter(
    Boolean
  ).length;
  const hasCertificates = certsConfigured === 2;

  // Get the tab from URL query params, default to "general"
  const tabFromUrl = searchParams.get('tab') || 'general';
  const [activeTab, setActiveTab] = useState(tabFromUrl);

  console.log('🔍 AdminSettingsPage - URL params:', {
    allParams: Object.fromEntries(searchParams.entries()),
    tabParam: searchParams.get('tab'),
    tabFromUrl,
    activeTab
  });

  // Update active tab when URL param changes
  useEffect(() => {
    const tab = searchParams.get('tab') || 'general';
    console.log('🔄 AdminSettingsPage - useEffect triggered, tab from URL:', tab);
    console.log('✅ AdminSettingsPage - Setting active tab to:', tab);
    setActiveTab(tab);
  }, [searchParams]);

  const handleSave = () => {
    Swal.fire({
      title: 'Éxito',
      text: 'Configuración guardada exitosamente',
      icon: 'success',
      confirmButtonText: 'Aceptar',
    });
  };

  console.log('🎨 AdminSettingsPage - Rendering with activeTab:', activeTab);

  return (
    <div className="font-display">
      {/* ============ CABECERA ============ */}
      <div>
        {/*
          El ultimo segmento del breadcrumb ES el titulo (h1) — sin eyebrow
          arriba (quedaba de mas junto al breadcrumb).
        */}
        <nav className="flex items-baseline gap-2">
          <span className="text-[14px] text-secondary-400">Inicio</span>
          <span className="text-[14px] text-secondary-300">/</span>
          <h1 className="text-2xl font-bold tracking-[-0.03em] text-ink-850 dark:text-white">
            Configuración Administrativa
          </h1>
        </nav>
        <p className="mt-2 text-sm text-secondary-500 dark:text-secondary-400">
          Administra las configuraciones del sistema
        </p>
      </div>

      {/* SUBSCRIPTIONS_DISABLED: <CurrentSubscriptionCard /> */}

      <Tabs
        value={activeTab}
        onValueChange={(newTab) => {
          console.log('📍 Tabs - onValueChange called with:', newTab);
          setActiveTab(newTab);
        }}
        className="mt-6 w-full"
        variant="pill"
      >
        <TabsList data-testid="admin-settings-tabs-list">
          <TabsTrigger value="general" icon={<Cog6ToothIcon className="h-3.5 w-3.5" />} data-testid="admin-settings-tab-general">
            Configuración General
          </TabsTrigger>
          <TabsTrigger value="fiscal" icon={<ShieldCheckIcon className="h-3.5 w-3.5" />} data-testid="admin-settings-tab-fiscal">
            Información Fiscal y Certificados
            {/* El badge sale del estado real de los certificados, no es decorativo. */}
            {!isLoadingCerts && !hasCertificates && (
              <TabsBadge tone="warn" active={activeTab === 'fiscal'}>
                Pendiente
              </TabsBadge>
            )}
          </TabsTrigger>
          <TabsTrigger value="templates" icon={<Squares2X2Icon className="h-3.5 w-3.5" />} data-testid="admin-settings-tab-templates">
            Templates
          </TabsTrigger>
        </TabsList>

        {/*
          ============ FRANJA DE ESTADO ============
          Fuera de los TabsContent porque resume la pantalla completa, no una
          pestaña. Los tres datos son reales (ver comentario de cabecera).
        */}
        <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
          {/* Certificados */}
          <div className={`${CARD} px-[22px] py-5`}>
            <div className={METRIC_LABEL}>Certificados</div>
            <div className="mt-2.5 flex items-center gap-3">
              <span
                className={`${SETTING_ICON} h-[38px] w-[38px] ${
                  hasCertificates
                    ? 'bg-teal-500/10 text-[#0d9488]'
                    : 'bg-accent-500/[.14] text-[#a15f0a]'
                }`}
              >
                {hasCertificates ? (
                  <CheckCircleIcon className="h-[18px] w-[18px]" />
                ) : (
                  <ExclamationTriangleIcon className="h-[18px] w-[18px]" />
                )}
              </span>
              <div className="min-w-0">
                <div className="text-sm font-bold text-ink-850 dark:text-white">
                  {isLoadingCerts
                    ? 'Consultando…'
                    : hasCertificates
                      ? 'Configurados'
                      : 'Pendientes de carga'}
                </div>
                <div className="mt-0.5 text-[12px] text-secondary-500 dark:text-secondary-400">
                  {certsConfigured} de 2 archivos configurados
                </div>
              </div>
            </div>
            <div className="mt-3 h-1 overflow-hidden rounded-full bg-[#eef2f6] dark:bg-secondary-700">
              <div
                className="h-full rounded-full bg-gradient-to-r from-teal-500 to-[#6b239e] transition-[width]"
                style={{ width: `${(certsConfigured / 2) * 100}%` }}
              />
            </div>
          </div>

          {/* Notificaciones */}
          <div className={`${CARD} px-[22px] py-5`}>
            <div className={METRIC_LABEL}>Notificaciones</div>
            <div className="mt-2.5 flex items-center gap-3">
              <span
                className={`${SETTING_ICON} h-[38px] w-[38px] ${
                  emailNotifications
                    ? 'bg-teal-500/10 text-[#0d9488]'
                    : 'bg-secondary-100 text-secondary-400 dark:bg-secondary-700'
                }`}
              >
                <EnvelopeIcon className="h-[18px] w-[18px]" />
              </span>
              <div className="min-w-0">
                <div className="text-sm font-bold text-ink-850 dark:text-white">
                  {emailNotifications ? 'Email activo' : 'Email desactivado'}
                </div>
                <div className="mt-0.5 text-[12px] text-secondary-500 dark:text-secondary-400">
                  Notificaciones por correo electrónico
                </div>
              </div>
            </div>
          </div>
        </div>

        <TabsContent value="general">
          <div className="mt-5">
            {/* ============ 01 · GENERAL ============ */}
            <div className={`${CARD} relative overflow-hidden px-8 pb-7 pt-8 ${TONE_SURFACE.neutral}`}>
              <span aria-hidden="true" className={`absolute inset-x-0 top-0 h-[3px] ${TONE_BAR.neutral}`} />
              <div className={SPLIT}>
                <SectionHead
                  num="01 · GENERAL"
                  icon={<DocumentTextIcon className="h-4 w-4" />}
                  tone="ink"
                  title="Configuración de Facturación"
                  desc="Ajustes que se aplican a cada nueva factura emitida por defecto."
                />

                <div className="flex flex-col gap-[18px]">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label htmlFor="invoicePrefix" className={FORM_LABEL}>
                        Prefijo de Facturas
                      </label>
                      <div className="relative mt-1.5">
                        <input
                          id="invoicePrefix"
                          type="text"
                          value={invoicePrefix}
                          onChange={(e) => setInvoicePrefix(e.target.value)}
                          className="w-full rounded-[10px] border border-[#ececec] bg-white py-[11px] pl-3.5 pr-[88px] font-mono text-sm font-bold tracking-[0.08em] text-ink-850 outline-none transition-colors placeholder:font-normal placeholder:tracking-normal placeholder:text-secondary-400 hover:border-secondary-300 focus:border-[#0d9488] focus:ring-[3px] focus:ring-teal-500/10 disabled:cursor-not-allowed disabled:bg-secondary-50 disabled:text-secondary-400 dark:border-secondary-600 dark:bg-secondary-800 dark:text-white dark:disabled:bg-secondary-900"
                          placeholder="FE"
                          disabled
                         data-testid="admin-settings-invoice-prefix-input"/>
                        {/* Ejemplo derivado del propio prefijo, no un valor inventado. */}
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded-md bg-[#f6f6f7] px-2 py-[3px] font-mono text-[12px] text-secondary-400 dark:bg-secondary-700">
                          {(invoicePrefix || 'FE') + '-1234'}
                        </span>
                      </div>
                      <div className={`mt-1.5 ${FIELD_HINT}`}>Prefijo para el número de factura</div>
                    </div>

                    <div>
                      <label htmlFor="defaultTaxRate" className={FORM_LABEL}>
                        Tasa de Impuesto por Defecto
                      </label>
                      <div className="relative mt-1.5">
                        <input
                          id="defaultTaxRate"
                          type="number"
                          value={defaultTaxRate}
                          onChange={(e) => setDefaultTaxRate(e.target.value)}
                          className="w-full rounded-[10px] border border-[#ececec] bg-white py-[11px] pl-3.5 pr-11 text-right font-mono text-base font-bold text-ink-850 outline-none transition-colors placeholder:font-normal placeholder:text-secondary-400 hover:border-secondary-300 focus:border-[#0d9488] focus:ring-[3px] focus:ring-teal-500/10 disabled:cursor-not-allowed disabled:bg-secondary-50 disabled:text-secondary-400 dark:border-secondary-600 dark:bg-secondary-800 dark:text-white dark:disabled:bg-secondary-900"
                          placeholder="7"
                          step="0.01"
                          disabled
                         data-testid="admin-settings-default-tax-rate-input"/>
                        <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-sm font-bold text-secondary-500">
                          %
                        </span>
                      </div>
                      <div className={`mt-1.5 ${FIELD_HINT}`}>
                        Tasa de impuesto aplicada por defecto
                      </div>
                    </div>
                  </div>

                  {/* Auto-aprobar facturas */}
                  <div className={`${INSET_PANEL} grid grid-cols-[20px_minmax(0,1fr)_auto] items-center gap-3.5 px-[18px] py-4`}>
                    <DesignCheckbox
                      id="autoApprove"
                      data-testid="admin-settings-auto-approve-checkbox"
                      checked={autoApprove}
                      onChange={(e) => setAutoApprove(e.target.checked)}
                      disabled
                    />
                    <label htmlFor="autoApprove" className="cursor-not-allowed">
                      <span className="block text-[14px] font-bold text-ink-850 dark:text-white">
                        Auto-aprobar facturas
                      </span>
                      <span className="mt-0.5 block text-xs leading-[1.5] text-secondary-500 dark:text-secondary-400">
                        Las facturas se aprobarán automáticamente sin revisión manual
                      </span>
                    </label>
                    <span className="rounded-full bg-[#f6f6f7] px-2.5 py-[3px] font-mono text-[11px] font-bold tracking-[0.12em] text-secondary-400 dark:bg-secondary-700">
                      {autoApprove ? 'ON' : 'OFF'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ============ 02 · CANALES ============ */}
            <div className="mt-5">
              {/* Notificaciones */}
              <div className={`${CARD} relative overflow-hidden px-8 pb-7 pt-8 ${TONE_SURFACE.teal}`}>
                <span aria-hidden="true" className={`absolute inset-x-0 top-0 h-[3px] ${TONE_BAR.teal}`} />
                <div className="flex items-start justify-between gap-3.5">
                  <SectionHead
                    num="02 · CANALES"
                    icon={<BellAlertIcon className="h-4 w-4" />}
                    tone="teal"
                    title="Notificaciones"
                    desc="Define cómo se avisa a los usuarios cuando cambia el estado de un documento."
                  />
                  <StatusPill tone={emailNotifications ? 'ok' : 'muted'}>
                    {emailNotifications ? '1 ACTIVA' : '0 ACTIVAS'}
                  </StatusPill>
                </div>

                <div className={`${SETTING_ROW} mt-3 pt-[18px]`}>
                  <span className={`${SETTING_ICON} bg-teal-500/10 text-[#0d9488]`}>
                    <EnvelopeIcon className="h-[18px] w-[18px]" />
                  </span>
                  <label htmlFor="emailNotifications" className="cursor-not-allowed">
                    <span className="block text-sm font-bold text-ink-850 dark:text-white">
                      Notificaciones por email
                    </span>
                    <span className="mt-0.5 block text-[13px] text-secondary-500 dark:text-secondary-400">
                      Recibir notificaciones por correo electrónico
                    </span>
                  </label>
                  <DesignToggle
                    data-testid="admin-settings-email-notifications-checkbox"
                    id="emailNotifications"
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                    disabled
                  />
                </div>
              </div>
            </div>

            {/*
              Barra de guardado flotante del diseño. Envuelve el mismo boton
              "Guardar Configuración" que ya estaba (handleSave). No se agrego
              "Descartar": no habia nada que descartara en la pantalla.
            */}
            <div className="sticky bottom-4 z-[5] mt-5 flex flex-wrap items-center justify-between gap-4 rounded-[14px] border border-[#ececec] bg-white/[.88] px-5 py-3.5 shadow-[0_1px_2px_rgba(10,34,51,.03),0_12px_32px_-18px_rgba(10,34,51,.24)] backdrop-blur-xl dark:border-secondary-700 dark:bg-secondary-800/[.88]">
              <div className="flex items-center gap-3">
                <span className="h-2 w-2 shrink-0 rounded-full bg-highlight-400 shadow-[0_0_8px_rgba(250,218,81,.6)]" />
                <span className="text-[13px] text-secondary-500 dark:text-secondary-400">
                  Los ajustes de <b className="font-bold text-ink-850 dark:text-white">Configuración General</b>{' '}
                  son de solo lectura por el momento.
                </span>
              </div>
              <button type="button" className={CTA} onClick={handleSave} data-testid="admin-settings-save-button">
                <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                Guardar Configuración
              </button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="fiscal">
          <div className="mt-5">
            <FiscalInfoTab />
          </div>
        </TabsContent>

        <TabsContent value="templates">
          <div className="mt-5">
            <ErrorBoundary>
              <TemplatesTab />
            </ErrorBoundary>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
